import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { applySeo, SEO_IMAGES } from '@/utils/seo';
import {
  injectJsonLd,
  clearJsonLd,
  buildWebSiteJsonLd,
  buildArticleJsonLd,
  buildFaqJsonLd,
  buildCollectionJsonLd,
} from '@/utils/schema';
import { ptArticles } from '@/mocks/articles/pt';
import { enArticles } from '@/mocks/articles/en';
import { deArticles } from '@/mocks/articles/de';
import { articleSlugs } from '@/mocks/articleList';

interface ArticleData {
  slug: string;
  title: string;
  metaDescription: string;
  faq: { q: string; a: string }[];
}

const articlesByLang: Record<string, Record<string, ArticleData>> = {
  pt: ptArticles as Record<string, ArticleData>,
  en: enArticles as Record<string, ArticleData>,
  de: deArticles as Record<string, ArticleData>,
};

const HOME_SEO: Record<
  string,
  { title: string; description: string; keywords: string; path: string }
> = {
  pt: {
    title: 'CSSBuy Spreadsheet 2026: Guias, Listas, QC, Frete e Ofertas',
    description:
      'Encontre CSSBuy Spreadsheet, listas de produtos, guias de compra, QC, custos de envio, armazém, cupons, segurança e comparações em um só lugar.',
    keywords: 'CSSBuy Spreadsheet, CSSBuy review, CSSBuy shipping, CSSBuy guide, CSSBuy coupon',
    path: '/',
  },
  en: {
    title: 'CSSBuy Spreadsheet 2026: Guides, Lists, QC, Shipping & Deals',
    description:
      'Find CSSBuy Spreadsheet, product lists, buying guides, QC, shipping costs, warehouse, coupons, safety and comparisons all in one place.',
    keywords: 'CSSBuy Spreadsheet, CSSBuy review, CSSBuy shipping, CSSBuy guide, CSSBuy coupon',
    path: '/en/',
  },
  de: {
    title: 'CSSBuy Spreadsheet 2026: Leitfäden, Listen, QC, Versand & Angebote',
    description:
      'Finden Sie CSSBuy Spreadsheet, Produktlisten, Kaufanleitungen, QC, Versandkosten, Lager, Coupons, Sicherheit und Vergleiche an einem Ort.',
    keywords: 'CSSBuy Spreadsheet, CSSBuy Review, CSSBuy Versand, CSSBuy Leitfaden, CSSBuy Coupon',
    path: '/de/',
  },
};

const PRODUCTS_SEO = {
  title: 'Product Catalog | CSSBuy Spreadsheet',
  description:
    'Browse the full CSSBuy Spreadsheet product catalog with 100+ items across shoes, hoodies, t-shirts, jackets and more. Click through to buy on the main store.',
  keywords: 'CSSBuy Spreadsheet products, CSSBuy catalog, CSSBuy shoes, CSSBuy hoodies, buy CSSBuy',
  path: '/products',
};

const ARTICLE_KEYWORDS =
  'CSSBuy Spreadsheet, CSSBuy guide, CSSBuy review, CSSBuy shipping, CSSBuy product research';

export default function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname || '/';

    if (pathname === '/products' || pathname === '/products/') {
      applySeo({ ...PRODUCTS_SEO, ogType: 'website', image: SEO_IMAGES.products });
      injectJsonLd([buildCollectionJsonLd()]);
      return;
    }

    const articleMatch = pathname.match(/^\/(artigo|article|artikel)\/?([^/]*)$/);
    if (articleMatch) {
      const segment = articleMatch[1];
      const lang = segment === 'artigo' ? 'pt' : segment === 'article' ? 'en' : 'de';
      const requestedSlug = articleMatch[2] || articleSlugs[0];
      const articles = articlesByLang[lang] || ptArticles;
      const article = articles[requestedSlug] || articles[articleSlugs[0]];
      const canonicalSlug = articles[requestedSlug] ? requestedSlug : articleSlugs[0];
      const path = `/${segment}/${canonicalSlug}`;

      applySeo({
        title: article.title,
        description: article.metaDescription,
        keywords: ARTICLE_KEYWORDS,
        path,
        ogType: 'article',
        image: SEO_IMAGES.article,
      });
      injectJsonLd([buildArticleJsonLd(article, path, lang), buildFaqJsonLd(article.faq)]);
      return;
    }

    let lang = 'pt';
    if (pathname.startsWith('/en')) lang = 'en';
    else if (pathname.startsWith('/de')) lang = 'de';
    else if (pathname !== '/' && !pathname.startsWith('/pt')) lang = 'unknown';

    if (lang === 'unknown') {
      document.title = 'Page Not Found | CSSBuy Spreadsheet';
      clearJsonLd();
      return;
    }

    const home = HOME_SEO[lang];
    applySeo({ ...home, ogType: 'website', image: SEO_IMAGES.home });
    injectJsonLd([buildWebSiteJsonLd(lang, home.description)]);
  }, [location.pathname]);

  return null;
}